import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  User,
  Mail,
  Shield,
  FileText,
  Info
} from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";

const Policy = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();

  return (
    <div className="min-h-screen bg-background">
      <Navbar 
        isAuthenticated={isAuthenticated} 
        isOwner={userType === "owner"} 
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Política de Uso e Reservas
              </h1>
              <p className="text-muted-foreground">
                Conheça as regras e procedimentos para reservas no Live Stay
              </p>
            </div>
          </div>

          {/* Aviso Importante sobre a Plataforma */}
          <Card className="mb-8 border-2 border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Info className="h-5 w-5 text-blue-600" />
                Sobre a Plataforma Live Stay
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-white rounded-lg border border-blue-100">
                <p className="text-base font-semibold text-foreground mb-3">
                  <strong className="text-red-600">IMPORTANTE:</strong> O site Live Stay não possui vínculo algum com a <strong>GAV Resorts</strong>.
                </p>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    A plataforma Live Stay foi criada por <strong className="text-foreground">proprietários de cotas Porto2Life</strong> com o intuito de:
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Apoiar todos os proprietários que também adquiriram cotas Porto2Life</li>
                    <li>Facilitar a divulgação de diárias disponíveis</li>
                    <li>Permitir a realização de trocas entre proprietários de cotas</li>
                  </ul>
                  <p className="mt-3 pt-3 border-t border-blue-100">
                    Esta é uma iniciativa independente da comunidade de proprietários, sem qualquer relação comercial ou institucional com a GAV Resorts.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Processo de Reserva */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Processo de Reserva
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-primary/5 rounded-lg">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-primary font-bold">1</span>
    </div>
                  <h3 className="font-semibold mb-2">Solicitação</h3>
                  <p className="text-sm text-muted-foreground">
                    O hóspede envia uma solicitação de reserva com as datas desejadas
                  </p>
    </div>
                
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-orange-600 font-bold">2</span>
    </div>
                  <h3 className="font-semibold mb-2">Avaliação</h3>
                  <p className="text-sm text-muted-foreground">
                    O proprietário tem 24 horas úteis para avaliar e responder
                  </p>
    </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-green-600 font-bold">3</span>
    </div>
                  <h3 className="font-semibold mb-2">Confirmação</h3>
                  <p className="text-sm text-muted-foreground">
                    Após aprovação, valores e pagamento são combinados diretamente
                  </p>
    </div>
    </div>
            </CardContent>
          </Card>

          {/* Regras Importantes */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Regras Importantes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">Solicitação não é garantia</h4>
                      <p className="text-sm text-muted-foreground">
                        Enviar uma solicitação não garante que a reserva será aprovada. 
                        O proprietário pode rejeitar por qualquer motivo.
                      </p>
    </div>
    </div>
                  
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">Prazo de 24 horas</h4>
                      <p className="text-sm text-muted-foreground">
                        O proprietário tem 24 horas úteis para entrar em contato após 
                        receber a solicitação.
                      </p>
    </div>
    </div>
                  
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">Conflito de datas</h4>
                      <p className="text-sm text-muted-foreground">
                        Se uma solicitação for aprovada, todas as outras para o mesmo 
                        período serão automaticamente rejeitadas.
                      </p>
    </div>
    </div>
    </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-purple-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">Contato direto</h4>
                      <p className="text-sm text-muted-foreground">
                        Após aprovação, hóspede e proprietário combinam valores e 
                        forma de pagamento diretamente.
                      </p>
    </div>
    </div>
                  
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-indigo-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">Responsabilidade</h4>
                      <p className="text-sm text-muted-foreground">
                        O Live Stay atua apenas como intermediário. A responsabilidade 
                        final é entre hóspede e proprietário.
                      </p>
    </div>
    </div>
                  
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-cyan-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">Disponibilidade</h4>
                      <p className="text-sm text-muted-foreground">
                        Imóveis com reservas aprovadas não aparecem mais nas buscas 
                        para aquelas datas.
                      </p>
    </div>
    </div>
    </div>
    </div>
            </CardContent>
          </Card>

          {/* Status das Solicitações */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Status das Solicitações
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                  <Badge variant="outline" className="text-orange-600 border-orange-200">
                    <Clock className="h-3 w-3 mr-1" />
                    Pendente
                  </Badge>
                  <p className="text-sm">
                    Aguardando resposta do proprietário (prazo de 24 horas úteis)
                  </p>
    </div>
                
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <Badge variant="outline" className="text-green-600 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Aprovada
                  </Badge>
                  <p className="text-sm">
                    Solicitação aprovada pelo proprietário. Entre em contato para combinar pagamento.
                  </p>
    </div>
                
                <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                  <Badge variant="outline" className="text-red-600 border-red-200">
                    <XCircle className="h-3 w-3 mr-1" />
                    Rejeitada
                  </Badge>
                  <p className="text-sm">
                    Solicitação rejeitada pelo proprietário ou automaticamente devido a conflito de datas.
                  </p>
    </div>
                
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Badge variant="outline" className="text-gray-600 border-gray-200">
                    <Clock className="h-3 w-3 mr-1" />
                    Expirada
                  </Badge>
                  <p className="text-sm">
                    Prazo de 24 horas expirado sem resposta do proprietário.
                  </p>
    </div>
    </div>
            </CardContent>
          </Card>

          {/* Informações de Contato */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                Informações de Contato
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-primary" />
                      <a 
                        href="mailto:livestaybrasil@gmail.com" 
                        className="text-primary hover:underline font-medium"
                      >
                        livestaybrasil@gmail.com
                      </a>
                    </p>
                    <p className="text-muted-foreground mt-2">
                      Este é o email de contato único para hóspedes e proprietários. 
                      Entre em contato para dúvidas, suporte ou informações sobre reservas.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Importante:</strong> Esta política pode ser atualizada a qualquer momento. 
                  Recomendamos que você consulte esta página regularmente para estar ciente de 
                  eventuais mudanças.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Policy;
